import React, { useEffect, useState } from 'react'
import DataCard, { DataCardProps } from '../../components/DataCard'
import ListCard from '../../components/ListCard'
import Modal from '../../components/Modal'
import ChatPanel from '../../features/chat/ChatPanel'
import ChatButton from '../../components/ChatButton'
import { TriangleAlert, Check, User, File, X, ChevronLeft, ChevronRight } from 'lucide-react'
import { useIsMobile, useIsVerified } from '../../utils/hooks'
import { useUser } from '../../context/UserContext';
import { useWorkspace } from '../../context/WorkspaceContext';
import { useQuery } from '@tanstack/react-query';
import formatCurrency from '../../utils/formatCurrency'

//API
import capitalApi from '../../api/capitalApi'

const STALE_TIME = 1000 * 60 * 5;    // Refetch on tab refocus every 5 min
const REFETCH_TIME = 1000 * 60 * 10; // Force refetch every 10 mins
// Refetch every time new data is sent

// SAMPLE DATA 
const actionData = [
    {
        title: 'Link additional data',
        subtitle: 'Upload your latest tax return to complete lender readiness.',
        time: '23:00'
    },
    {
        title: 'Complete your capital profile',
        subtitle: 'You’re 2 steps away from unlocking matched offers.',
        time: '23:00'
    }
];
const readinessData = [
    {
        title: 'Business Profile',
        subtitle: 'Basic entity info, NAICS code and incorporation details.',
        complete: true
    },
    {
        title: 'Owner Identity',
        subtitle: 'KYC requirements for risk and compliance validation.',
        complete: false
    },
    {
        title: 'Business Bank Statements',
        subtitle: 'Pulled automatically from your connected bank account.',
        complete: true
    },
    {
        title: 'Profit and Loss Statements (3 Months)',
        subtitle: 'Pulled directly from your connected QuickBooks account.',
        complete: true
    },
    {
        title: 'Business Tax Return',
        subtitle: 'Used to verify reported income and cross-check financials.',
        complete: false
    },
    {
        title: 'Lending Consent',
        subtitle: 'Allows us to securely share your profile with matched lenders.',
        complete: true
    }
];


export default function Capital() {
    const isMobile = useIsMobile()
    const [showModal, setShowModal] = useState(false)
    const [showChat, setShowChat] = useState(!isMobile)
    const [extendChat, setExtendChat] = useState(false)
    const [showSetupGuidePanel, setShowSetupGuidePanel] = useState(false)
    const isVerified = useIsVerified()
    const { isAdmin } = useUser();
    const { activeBusinessId } = useWorkspace();

    useEffect(() => {
        if (isMobile) {
            setShowChat(false);
            setExtendChat(false);
        }
    }, [isMobile]);

    useEffect(() => {
        const handleSetupGuideVisibility = (event: Event) => {
            setShowSetupGuidePanel(Boolean((event as CustomEvent<{ open?: boolean }>).detail?.open));
        };

        setShowSetupGuidePanel(document.body.classList.contains('finz-setup-guide-open'));
        window.addEventListener('finz:setup-guide:visibility', handleSetupGuideVisibility);
        return () => window.removeEventListener('finz:setup-guide:visibility', handleSetupGuideVisibility);
    }, []);

    const {
        data: capitalData,
        isLoading: isCapitalDataLoading,
        isError: isCapitalDataError,
        refetch: refetchCapitalData,
    } = useQuery({
        queryKey: ['capitalData', activeBusinessId],
        queryFn: () => capitalApi.getCapitalMetrics(activeBusinessId || undefined),
        staleTime: STALE_TIME,
        refetchInterval: REFETCH_TIME,
        retry: false,
    });

    const {
        data: capitalStatus,
        isLoading: isCapitalStatusLoading,
        isError: isCapitalStatusError,
        refetch: refetchCapitalStatus,
    } = useQuery({
        queryKey: ['capitalStatus', activeBusinessId],
        queryFn: () => capitalApi.getCapitalStatus(activeBusinessId || undefined),
        staleTime: STALE_TIME,
        refetchInterval: REFETCH_TIME,
        retry: false,
    });

    console.log(capitalStatus)

    let stage = 0
    if (capitalStatus && capitalStatus.stage === 'Funding Profile') {
        stage = 1
    } else if (capitalStatus && capitalStatus.stage === 'Lender Matching') {
        stage = 2
    } else if (capitalStatus && capitalStatus.stage === 'Capital Approved') {
        stage = 3
    }

    let transformedData: DataCardProps[] = [];
    let transformedCapital: DataCardProps = { title: 'Eligible Capital', data: '-', statusText: '-' }
    if (capitalData) {
        transformedCapital = { title: 'Eligible Capital', data: formatCurrency(capitalData.eligible_capital), status: '', statusText: 'Estimated' }
        transformedData = [
            { title: 'Net Cash Flow', data: formatCurrency(capitalData.net_cash_flow), status: '', statusText: capitalData.net_cash_flow_status },
            { title: 'Revenue Stability', data: Math.round(capitalData.revenue_stability) + '%', status: '', statusText: capitalData.revenue_stability_status },
            { title: 'Payment History', data: Math.round(capitalData.payment_history) + '%', status: '', statusText: capitalData.payment_history_status },
            { title: 'Runway', data: capitalData.runway_days + 'D', statusStyle: 'dot', status: '', statusText: capitalData.runway_status },
            { title: 'Credit Utilization', data: Math.round(capitalData.credit_utilization) + '%', statusStyle: 'dot', status: '', statusText: capitalData.credit_utilization_status },
            { title: 'Debt Coverage', data: capitalData.debt_coverage_ratio + 'x', statusStyle: 'dot', status: '', statusText: capitalData.debt_coverage_status }
        ];
    }

    const handleShowChat = () => {
        setShowChat(true)
    }

    return (
        <div className="flex relative h-full overflow-hidden">

            {!isVerified && (
                <div className="disabled-view" />
            )}


            {/* MAIN CAPITAL DASHBOARD */}
            <div className={`overflow-auto flex-1 ${showChat && !showSetupGuidePanel && !isMobile ? 'w-[70%]' : 'w-full'}`}>
                <div className={`md:min-w-[1000px] min-w-[400px] m-5`}>

                    {/* UPLOAD MODAL */}
                    {showModal && (
                        <Modal onClose={() => setShowModal(false)}>
                            <div className="w-full font-semibold text-left mb-5">Upload</div>

                            <div className="flex flex-col gap-5 mb-5">
                                <button className="hover:bg-gray-100 rounded p-1">
                                    <ListCard
                                        title="Owner Identity"
                                        subtitle="KYC requirements for risk and compliance validation."
                                        icon={<User size={25} />}
                                    />
                                </button>
                                <button className="hover:bg-gray-100 rounded p-1">
                                    <ListCard
                                        title="Business Tax Return"
                                        subtitle="Used to verify reported income and cross-check financials."
                                        icon={<File size={25} />}
                                    />
                                </button>
                            </div>

                            <div className="flex justify-center gap-4">
                                <button className="w-48 button-1 button-disabled">Continue</button>
                            </div>
                        </Modal>
                    )}


                    {/* TOP SECTION -----------------------------*/}
                    <div className="flex flex-wrap md:grid md:grid-cols-[1.5fr_3fr] gap-4 mb-4">
                        {/* Eligible Capital */}
                        <DataCard title={transformedCapital.title} data={transformedCapital.data} statusText={'Estimated'} />

                        {/* Step Progress */}
                        <div className="w-full !py-12 md:!w-auto dashboard-card flex flex-col justify-center">
                            <div className="flex justify-between items-center mb-2">
                                <span className="dashboard-header !mb-0">You're on Step 1</span>
                                <span className="text-xl font-bold text-gray-800">
                                    {capitalStatus && Math.round(capitalStatus.progress_percentage) + "%"}
                                </span>
                            </div>

                            <div className="flex font-medium gap-4 ">
                                <div className="flex-1">
                                    <div className={`h-1.5 rounded-full ${stage >= 1 ? 'bg-indigo-400' : 'bg-gray-200'}`}></div>
                                    <p className="text-center text-sm mt-1">Funding Profile</p>
                                </div>
                                <div className="flex-1">
                                    <div className={`h-1.5 rounded-full ${stage >= 2 ? 'bg-indigo-400' : 'bg-gray-200'}`}></div>
                                    <p className="text-center text-sm mt-1">Lender Matching</p>
                                </div>
                                <div className="flex-1">
                                    <div className={`h-1.5 rounded-full ${stage >= 3 ? 'bg-indigo-400' : 'bg-gray-200'}`}></div>
                                    <p className="text-center text-sm mt-1">Capital Approved</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-wrap gap-4 md:grid md:grid-cols-[1.5fr_1fr]">
                        <div className="flex flex-col md:w-auto w-full gap-4">
                            {/* MIDDLE DATA CARDS -----------------------------*/}
                            <div className="flex grid md:grid-cols-3 md:w-auto grid-cols-2 gap-4">
                                {transformedData.map((item, idx) => (
                                    <DataCard
                                        key={idx}
                                        title={item.title}
                                        data={item.data}
                                        status={item.status}
                                        statusStyle={item.statusStyle}
                                        statusText={item.statusText}
                                        subdata={item.subdata}
                                    />
                                ))}
                            </div>

                            {/* BOTTOM ACTION ITEMS -----------------------------*/}
                            <div className="dashboard-card flex flex-col flex-1">
                                <div className="dashboard-header !mb-5">Action Items</div>
                                <div className="flex flex-col gap-4">
                                    {actionData.map((item, idx) => (
                                        <button className="hover:bg-gray-100 p-1 rounded">
                                            <ListCard
                                                key={idx}
                                                title={item.title}
                                                subtitle={item.subtitle}
                                                time={item.time}
                                                titleStyle={'underline'}
                                            />
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* SIDE CREDIT READINESS -----------------------------*/}
                        <div className="dashboard-card !pb-12 flex flex-col justify-between">
                            <div>
                                <div className="dashboard-header !mb-1">Credit Readiness</div>
                                <div className="mb-5 font-medium">Progress: 4 of 6 Complete</div>
                                <div className="flex flex-col gap-5 mb-20">
                                    {readinessData.map((item, idx) => (
                                        <ListCard
                                            key={idx}
                                            title={item.title}
                                            subtitle={item.subtitle}
                                            iconContainer={false}
                                            icon={
                                                item.complete ? (
                                                    <div className="logo-container !w-9 !bg-green-400">
                                                        <Check size={22} className="text-white" />
                                                    </div>
                                                ) : (
                                                    <div className="logo-container !w-9 !bg-amber-400">
                                                        <TriangleAlert size={24} className="text-white" />
                                                    </div>
                                                )
                                            }
                                        />
                                    ))}
                                </div>
                            </div>

                            {/* Upload Button */}
                            <div className="w-full text-center">
                                <button onClick={() => setShowModal(true)} className="button-1 w-48">Upload</button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {!isAdmin && showSetupGuidePanel && !isMobile && (
                <div
                    aria-hidden="true"
                    className="relative h-full w-[30%] max-w-[300px] min-w-[350px] shrink-0 border bg-white shadow-sm"
                />
            )}

            {/* CHAT PANEL */}
            {!isAdmin && <div className={`${showChat && !showSetupGuidePanel ? (isMobile ? 'fixed inset-0 z-[1999] flex bg-white w-full max-w-none h-[100dvh]' : 'flex bg-white') : 'hidden'} ${extendChat ? 'w-[40%] max-w-[600px]' : 'w-[30%] max-w-[300px]'} chat-panel`}>
                {/* Chat */}
                <div className="flex-1 overflow-auto flex flex-col">
                    <ChatPanel
                        page={'capital'}
                        onClose={() => setShowChat(false)}
                    />
                </div>
                {/* Extend Button */}
                {!isMobile && (
                    <div onClick={() => setExtendChat(!extendChat)} className="border hover:bg-gray-200 hover:cursor-pointer py-3 rounded absolute bg-gray-100 top-10 -left-2">
                        {extendChat ? <ChevronRight size={18} className="text-gray-500" /> : <ChevronLeft size={18} className="text-gray-500" />}
                    </div>
                )}
            </div>}

            {/* Chat Toggle Button */}
            {(!showChat && !showSetupGuidePanel && !isAdmin) && <ChatButton handleShowChat={() => setShowChat(true)} />}
        </div>
    )
}
